import { Children, Fragment, forwardRef, isValidElement, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useListFlip } from '../../hooks/panel/useListFlip';
import { useAnimatedPanelSize } from '../../hooks/panel/useAnimatedPanelSize';

function flattenToolbarChildren(children, prefix = 'toolbar') {
  return Children.toArray(children).flatMap((child, index) => {
    const fallbackKey = `${prefix}-${index}`;
    if (isValidElement(child) && child.type === Fragment) {
      const fragmentKey = child.key != null ? `${prefix}-${String(child.key)}` : fallbackKey;
      return flattenToolbarChildren(child.props?.children, fragmentKey);
    }

    return [{
      child,
      key: isValidElement(child) && child.key != null ? `${prefix}-${String(child.key)}` : fallbackKey,
    }];
  });
}

const CompactModeSurface = forwardRef(function CompactModeSurface(
  {
    bodyContent = null,
    contentBeforeToolbar = null,
    toolbarContent = null,
    contentAfterToolbar = null,
    refsPortalTarget = null,
    dragOver = false,
    dropHint = null,
    onDragEnter,
    onDragOver,
    onDragLeave,
    onDrop,
    className = '',
    animateToolbarChanges = false,
  },
  ref,
) {
  const normalizedDropHint = dropHint && typeof dropHint === 'string'
    ? { text: dropHint, tone: 'info' }
    : (dropHint?.text
        ? { text: dropHint.text, tone: dropHint.tone === 'reject' ? 'reject' : 'info' }
        : null);
  const surfaceRef = useRef(null);
  const toolbarRef = useRef(null);
  const { capturePositions, animateFromCapture } = useListFlip(toolbarRef);
  const bodyNode = bodyContent
    ? (refsPortalTarget ? ReactDOM.createPortal(bodyContent, refsPortalTarget) : bodyContent)
    : null;
  const toolbarItems = useMemo(
    () => flattenToolbarChildren(toolbarContent).map(({ child, key }) => {
      const childStyle = isValidElement(child) ? child.props?.style : null;
      const wrapperStyle = childStyle
        ? {
            flex: childStyle.flex,
            flexGrow: childStyle.flexGrow,
            flexShrink: childStyle.flexShrink,
            flexBasis: childStyle.flexBasis,
            minWidth: childStyle.minWidth,
            width: childStyle.width,
          }
        : undefined;

      return (
        <div
          key={key}
          className="compact-mode-toolbar__item"
          data-module-id={key.replace(/[^a-zA-Z0-9_-]/g, '_')}
          data-composer-feature-item="true"
          data-composer-feature-key={key.replace(/[^a-zA-Z0-9_-]/g, '_')}
          style={wrapperStyle}
        >
          {child}
        </div>
      );
    }),
    [toolbarContent],
  );

  // Toolbar child identity in DOM order. `toolbarItems` itself is a fresh array on every
  // parent render (its memo key is a JSX children prop), so depending on the reference
  // would leave this effect running every render exactly as it did with no dep array.
  // The joined key string changes on an insert, a removal, or a reorder — which is the
  // full set of changes a FLIP has anything to animate — and on nothing else.
  const toolbarLayoutKey = useMemo(
    () => toolbarItems.map((item) => item.key).join('|'),
    [toolbarItems],
  );

  useLayoutEffect(() => {
    if (!animateToolbarChanges || !toolbarLayoutKey) {
      return undefined;
    }

    animateFromCapture();
    return undefined;
  }, [animateFromCapture, animateToolbarChanges, toolbarLayoutKey]);

  useEffect(() => {
    if (!animateToolbarChanges) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      if (
        target.closest('.compact-mode-toolbar')
        || target.closest('.dropdown-popover-content--compact')
      ) {
        capturePositions();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, [animateToolbarChanges, capturePositions]);

  useAnimatedPanelSize(surfaceRef, {
    enabled: animateToolbarChanges,
    measureRef: toolbarRef,
    className: 'compact-mode-content--resizing',
    durationMs: 220,
  });

  return (
    <div
      ref={(node) => {
        surfaceRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      }}
      className={`compact-mode-content${className ? ` ${className}` : ''}`}
      data-compact-surface="true"
      data-drag-over={dragOver ? 'true' : undefined}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {normalizedDropHint ? (
        <div className={`prompt-bar__drop-hint prompt-bar__drop-hint--${normalizedDropHint.tone}`}>
          <span>{normalizedDropHint.text}</span>
        </div>
      ) : null}

      {bodyNode}
      {contentBeforeToolbar}

      {toolbarContent ? (
        <div
          ref={toolbarRef}
          className="compact-mode-toolbar"
          data-compact-toolbar="true"
          data-composer-feature-row="true"
        >
          {toolbarItems}
        </div>
      ) : null}

      {contentAfterToolbar}
    </div>
  );
});

export default CompactModeSurface;
